import { dateAdapter } from './helpers';
import { DatePickerOverrides, FieldWrapper, InputWrapper } from './styled';
import localization from '@duetds/date-picker/dist/collection/components/duet-date-picker/date-localization';
import { useField } from 'formik';
import React, { useEffect, useRef, memo } from 'react';
import Input from '@service-victoria/component-library/components/Date/Input';
import { Label, ErrorLabel } from '@service-victoria/component-library/components/Input/styled';
import { DuetDateElement } from 'types/globals';

function useListener(ref, eventName, handler) {
  useEffect(() => {
    if (ref.current) {
      const element = ref.current;
      element.addEventListener(eventName, handler);
      return () => element.removeEventListener(eventName, handler);
    }
  }, [eventName, handler, ref]);
}

type DatePickerProps = {
  onChange?: (e: any) => void;
  onFocus?: (e: any) => void;
  onBlur?: (e: any) => void;
  onOpen?: (e: any) => void;
  onClose?: (e: any) => void;
  max?: string;
  min?: string;
  name?: string;
  value?: string;
};

type FormikDatePickerProps = DatePickerProps & {
  name: string;
  label: string;
  errorLabelProps?: React.ComponentProps<typeof ErrorLabel>;
};

function scrollDialogIntoView(ref: React.RefObject<DuetDateElement>, callback?: (e: any) => void) {
  return (e: any) => {
    if (callback) {
      callback(e);
    }
    const { height, top } =
      ref.current?.querySelector('.duet-date__dialog')?.getBoundingClientRect() || {};
    if (height && top && height + top > window.innerHeight) {
      document.scrollingElement?.scrollBy?.(0, top + height - window.innerHeight + 40);
    }
  };
}

export const DatePicker: React.FC<DatePickerProps> = ({
  onChange,
  onFocus,
  onBlur,
  onOpen,
  onClose,
  ...props
}) => {
  const ref = useRef<DuetDateElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.localization = {
        ...localization,
        placeholder: 'DD/MM/YYYY'
      };
      ref.current.dateAdapter = dateAdapter;
    }
  }, []);

  useListener(ref, 'duetChange', onChange);
  useListener(ref, 'duetFocus', onFocus);
  useListener(ref, 'duetBlur', onBlur);
  useListener(ref, 'duetOpen', scrollDialogIntoView(ref, onOpen));
  useListener(ref, 'duetClose', onClose);

  return (
    <>
      <duet-date-picker ref={ref} {...props}></duet-date-picker>
      <DatePickerOverrides />
    </>
  );
};

export const FormikDatePicker: React.FC<FormikDatePickerProps> = ({
  name,
  label,
  onChange,
  onBlur,
  onClose,
  errorLabelProps,
  ...props
}) => {
  const [field, meta, actions] = useField<string>(name);

  const showError = !!(meta.error && meta.touched);

  return (
    <FieldWrapper>
      <Label htmlFor={name} id={`${name}.label`}>
        {label}
      </Label>
      <InputWrapper>
        <Input
          disabled={false}
          field={{
            ...field,
            onChange: e => {
              const ymdValue = dmyToYmd(e.target.value);
              actions.setValue(ymdValue);
            },
            value: ymdToDmy(field.value)
          }}
          meta={meta}
          divider='/'
        />
      </InputWrapper>
      <DatePicker
        aria-labelledby={`${name}.label`}
        aria-invalid={showError}
        onChange={e => {
          field.onChange(e);

          if (onChange) {
            onChange(e);
          }
        }}
        onBlur={e => {
          field.onBlur(e);

          if (onBlur) {
            onBlur(e);
          }
        }}
        onClose={e => {
          field.onBlur(e);

          if (onClose) {
            onClose(e);
          }
        }}
        value={field.value}
        name={name}
        {...props}
      />
      {showError && <ErrorLabel {...errorLabelProps}>{meta.error}</ErrorLabel>}
    </FieldWrapper>
  );
};

function dmyToYmd(value: string) {
  return value
    .split('/')
    .reverse()
    .map(i => i.trim())
    .join('-');
}

function ymdToDmy(value: string) {
  return value
    .split('-')
    .reverse()
    .map(v => v.trim())
    .join('/');
}

export default memo(FormikDatePicker);
